// LocalStorage tabanlı fiyat teklifi hafızası.
// PriceQuoteModal kaydeder, AppointmentDetailEdit (veya başka consumer) okur ve aktarır.
// Bir teklif birden fazla birim içerebilir: operationList düz bir listedir (her kalem
// operation.unitId taşır) ve her birimin kendi kurum/randevu türü seçimi
// unitSelections[unitId] altında tutulur. Eski tek birimli kayıtlarla uyum için
// top-level unitId/institutionId/appointmentTypeId de yazılmaya devam eder.
// Aynı tab içinde değişimleri yakalamak için 'price-quote-changed' custom event'i fırlatılır.
const KEY = 'price-quote-memory';
const EVENT_NAME = 'price-quote-changed';

const emitChange = () => {
    try {
        window.dispatchEvent(new Event(EVENT_NAME));
    } catch {}
};

export const PriceQuoteStorage = {
    save: (data) => {
        try {
            localStorage.setItem(KEY, JSON.stringify({
                ...data,
                savedAt: new Date().toISOString()
            }));
            emitChange();
            return true;
        } catch (e) {
            console.error('PriceQuoteStorage.save failed', e);
            return false;
        }
    },

    get: () => {
        try {
            const raw = localStorage.getItem(KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            console.error('PriceQuoteStorage.get failed', e);
            return null;
        }
    },

    has: () => {
        try {
            const raw = localStorage.getItem(KEY);
            if (!raw) return false;
            const data = JSON.parse(raw);
            const ops = data?.operationList?.length || 0;
            const others = data?.otherOperations?.length || 0;
            return (ops + others) > 0;
        } catch {
            return false;
        }
    },

    // Belirli bir birim için o birime ait kurum/randevu türü seçimini döner.
    // Yeni format: data.unitSelections = { [unitId]: { institutionId, appointmentTypeId, ... } }
    // Eski format (tek birimli teklifler): top-level unitId/institutionId/appointmentTypeId.
    getSelectionForUnit: (data, target) => {
        const fromMap = data?.unitSelections?.[target];
        if (fromMap) return fromMap;
        if (Number(data?.unitId) === target) {
            return {
                unitId: data.unitId,
                unitName: data.unitName,
                firmName: data.firmName,
                institutionId: data.institutionId,
                appointmentTypeId: data.appointmentTypeId,
            };
        }
        return null;
    },

    // Belirli bir birim için kullanılabilir operationList döner.
    // Saved op.operation.unitId === unitId olanlar alınır.
    // Hiç eşleşme yoksa null döner (consumer butonu gizlemeli).
    // unitId URL query'den string, API'den number gelebildiği için sayısal karşılaştırma.
    getForUnit: (unitId) => {
        if (!unitId) return null;
        const data = PriceQuoteStorage.get();
        if (!data) return null;
        const target = Number(unitId);
        if (Number.isNaN(target)) return null;
        const matchedOps = (data.operationList || []).filter(
            x => Number(x?.operation?.unitId) === target
        );
        if (matchedOps.length === 0) return null;

        // Teklif çok birimli olabilir; bu birimin kendi kurumu/randevu türü kullanılır.
        const selection = PriceQuoteStorage.getSelectionForUnit(data, target);
        return {
            ...data,
            operationList: matchedOps,
            unitName: selection?.unitName ?? data.unitName,
            firmName: selection?.firmName ?? data.firmName,
            institutionId: selection?.institutionId ?? null,
            appointmentTypeId: selection?.appointmentTypeId ?? null,
            // Bu birim için kurum/randevu türü seçimi var mı?
            unitMatches: !!selection,
        };
    },

    // Verilen birim DIŞINDA hafızada bekleyen kalemlerin özeti.
    // Çok birimli tekliflerde "başka birime ait teklif var" uyarısı için kullanılır.
    getOtherUnitsSummary: (unitId) => {
        const data = PriceQuoteStorage.get();
        if (!data) return null;
        const target = Number(unitId);
        const otherOps = (data.operationList || []).filter(
            x => Number(x?.operation?.unitId) !== target
        );
        const opCount = otherOps.length + (data.otherOperations?.length || 0);
        if (opCount === 0) return null;

        const unitNames = [];
        otherOps.forEach(x => {
            const selection = PriceQuoteStorage.getSelectionForUnit(data, Number(x?.operation?.unitId));
            const name = selection?.unitName;
            if (name && !unitNames.includes(name)) unitNames.push(name);
        });

        return {
            unitNames,
            unitName: unitNames.join(', ') || data.unitName || '',
            firmName: unitNames.length > 1 ? '' : (data.firmName || ''),
            opCount,
        };
    },

    clear: () => {
        localStorage.removeItem(KEY);
        emitChange();
    },

    // Belirli bir birime aktarım yapıldığında sadece o birime ait operations'ı + tüm
    // otherOperations'ı (her aktarımda gittikleri için) hafızadan çıkarır.
    // Birim metadata (unitId/unitName/institutionId/appointmentTypeId/currency) korunur.
    removeTransferredForUnit: (unitId) => {
        const data = PriceQuoteStorage.get();
        if (!data) return;
        const target = Number(unitId);
        if (Number.isNaN(target)) return;
        const remaining = (data.operationList || []).filter(
            x => Number(x?.operation?.unitId) !== target
        );
        // Bu birimin kurum/randevu türü seçimi de artık gerekmiyor
        const remainingSelections = { ...(data.unitSelections || {}) };
        delete remainingSelections[target];
        const newData = {
            ...data,
            operationList: remaining,
            unitSelections: remainingSelections,
            otherOperations: [],
        };
        try {
            localStorage.setItem(KEY, JSON.stringify(newData));
            emitChange();
        } catch (e) {
            console.error('PriceQuoteStorage.removeTransferredForUnit failed', e);
        }
    },

    // Consumer'lar buna abone olur (cleanup unsubscribe döner)
    subscribe: (callback) => {
        const handler = () => callback(PriceQuoteStorage.has());
        window.addEventListener(EVENT_NAME, handler);
        window.addEventListener('storage', handler); // başka tab'tan değişim
        return () => {
            window.removeEventListener(EVENT_NAME, handler);
            window.removeEventListener('storage', handler);
        };
    }
};
